import { dayShortNames } from "@/shared/consts/day-names"
import { monthsGenitive } from "@/shared/consts/month-names"

const ISO_WALL_TIME_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?$/

interface ParsedWallDate {
    year: string
    month: string
    day: string
    hours: string
    minutes: string
    monthIndex: number
    dayOfMonth: number
    weekDay: number
}

function parseWallDate(value: string): ParsedWallDate | null {
    const match = value.match(ISO_WALL_TIME_RE)
    if (!match) return null

    const [, year, month, day, hours, minutes] = match
    const yearNumber = Number(year)
    const monthNumber = Number(month)
    const dayNumber = Number(day)
    const hoursNumber = Number(hours)
    const minutesNumber = Number(minutes)

    if (
        Number.isNaN(yearNumber) ||
        Number.isNaN(monthNumber) ||
        Number.isNaN(dayNumber) ||
        Number.isNaN(hoursNumber) ||
        Number.isNaN(minutesNumber) ||
        monthNumber < 1 ||
        monthNumber > 12 ||
        dayNumber < 1 ||
        dayNumber > 31 ||
        hoursNumber > 23 ||
        minutesNumber > 59
    ) {
        return null
    }

    const encodedDate = new Date(Date.UTC(yearNumber, monthNumber - 1, dayNumber))
    if (
        encodedDate.getUTCFullYear() !== yearNumber ||
        encodedDate.getUTCMonth() !== monthNumber - 1 ||
        encodedDate.getUTCDate() !== dayNumber
    ) {
        return null
    }

    return {
        year,
        month,
        day,
        hours,
        minutes,
        monthIndex: monthNumber - 1,
        dayOfMonth: dayNumber,
        weekDay: encodedDate.getUTCDay(),
    }
}

function parseDateFallback(value: string): ParsedWallDate | null {
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return null

    const year = String(parsed.getFullYear())
    const monthIndex = parsed.getMonth()
    const dayOfMonth = parsed.getDate()
    const month = String(monthIndex + 1).padStart(2, "0")
    const day = String(dayOfMonth).padStart(2, "0")
    const hours = String(parsed.getHours()).padStart(2, "0")
    const minutes = String(parsed.getMinutes()).padStart(2, "0")

    return {
        year,
        month,
        day,
        hours,
        minutes,
        monthIndex,
        dayOfMonth,
        weekDay: parsed.getDay(),
    }
}

function resolveWallDate(value: string | null | undefined): ParsedWallDate | null {
    if (!value) return null
    return parseWallDate(value) ?? parseDateFallback(value)
}

export function formatFlightWallTime(value: string | null | undefined): string {
    const parsed = resolveWallDate(value)
    if (!parsed) return "—"
    return `${parsed.day}.${parsed.month}.${parsed.year}, ${parsed.hours}:${parsed.minutes}`
}

export interface FlightWallDateInfo {
    time: string
    day: string
    monthShort: string
    weekdayShort: string
}

export function getFlightWallDateInfo(value: string | null | undefined): FlightWallDateInfo | null {
    const parsed = resolveWallDate(value)
    if (!parsed) return null

    return {
        time: `${parsed.hours}:${parsed.minutes}`,
        day: String(parsed.dayOfMonth),
        monthShort: monthsGenitive[parsed.monthIndex].slice(0, 3).toLowerCase(),
        weekdayShort: dayShortNames[parsed.weekDay].toLowerCase(),
    }
}
